import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "helpers/jwt-token-access/customBaseQuery";
import { ProjectType } from "./type";

const convertToFormData = (obj: any) => {
  const formData = new FormData();
  Object.keys(obj).forEach((key) => {
    if (Array.isArray(obj[key])) {
      obj[key].forEach((item: any) => formData.append(key, item));
    } else {
      formData.append(key, obj[key]);
    }
  });
  return formData;
};

export const projectSlice = createApi({
  reducerPath: "project",
  baseQuery: customBaseQuery,
  tagTypes: [
    "GetAllProjects",
    "GetProjectsByUserRole",
    "GetTeamByProject",
    "GetProjectsById",
    "GetProjectBudgets",
    "GetProjectProductAssignments",
  ],
  endpoints: (builder) => ({
    createProject: builder.mutation({
      query: (newTask) => {
        const formData = convertToFormData(newTask);
        return {
          url: "/organization/api/v1/projects/",
          method: "POST",
          body: formData,
        };
      },
    }),
    updateProject: builder.mutation({
      query: ({ id, newProject }: { id: number; newProject: any }) => {
        if (newProject.thumbnail === null) {
          delete newProject.thumbnail;
        }

        const formData = convertToFormData(newProject);
        return {
          url: `/organization/api/v1/projects/${id}/`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: ["GetAllProjects", "GetProjectsByUserRole", "GetProjectsById"],
    }),
    getAllProjects: builder.query<ProjectType[], void>({
      query: () => "/organization/api/v1/projects/",
      providesTags: ["GetAllProjects"],
    }),
    getAllProjectsByUserRole: builder.query({
      query: (page) => `/organization/api/v1/projects_by_user_role/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&status=${page.status}`,
      providesTags: ["GetProjectsByUserRole"],
    }),
    getProjectsByAssignedUser: builder.query({
      query: () => `/organization/api/v1/project_by_assigned_user/`,
      providesTags: ["GetProjectsByUserRole"],
    }),
    getprojectById: builder.query<ProjectType, string | undefined>({
      query: (id) => `/organization/api/v1/projects/${id}/`,
      providesTags: ["GetProjectsById"],
    }),
    getProjectByEmployeeId: builder.query<ProjectType[], string | undefined>({
      query: (employee_id) =>
        `/organization/api/v1/projects/?employee_id=${employee_id}`,
    }),
    getTeamByProjectId: builder.query<ProjectType[], string | undefined>({
      query: (project_id) => `/organization/api/v1/team/?project=${project_id}`,
      providesTags: ["GetTeamByProject"],
    }),
    getProjectsByDropDown: builder.query({
      query: () => `/organization/api/v1/projects_by_user_drop_down/`,
      providesTags: ["GetProjectsByUserRole"],
    }),
    createTeam: builder.mutation({
      query: ({ newTeam }: { newTeam: any }) => {
        return {
          url: `/organization/api/v1/team/`,
          method: "POST",
          body: newTeam,
        };
      },
      invalidatesTags: ["GetTeamByProject"],
    }),
    updateTeam: builder.mutation({
      query: ({ id, newTeam }: { id: any; newTeam: any }) => {
        return {
          url: `/organization/api/v1/team/${id}/`,
          method: "PATCH",
          body: newTeam,
        };
      },
      invalidatesTags: ["GetTeamByProject", "GetProjectsById"],
    }),
    // getAllPriority: builder.query<PriorityType[], void>({
    //   query: () => "/task/api/v1/task_priority/",
    // }),
    getProjectAttachmentById: builder.query<any[], string | undefined>({
      query: (id) => `/organization/api/v1/projects_attachment/${id}/`,
    }),

    getAllProjectAttachments: builder.query<any[], string | undefined>({
      query: (id) => `/organization/api/v1/projects_attachment/?project=${id}`,
    }),

    createProjectAttachment: builder.mutation({
      query: (newAttachment) => {
        const formData = convertToFormData(newAttachment);
        return {
          url: "/organization/api/v1/projects_attachment/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["GetAllProjects", "GetProjectsByUserRole"],
    }),

    deleteProjectAttachmentById: builder.mutation({
      query: (id: string) => ({
        url: `/organization/api/v1/projects_attachment/${id}/`,
        method: "DELETE",
      }),
    }),

    deleteTeamById: builder.mutation({
      query: (id: string) => ({
        url: `/organization/api/v1/team/${id}/`,
        method: "DELETE",
      }),
    }),

    // Project Budget (sections by project; paginated + search)
    getProjectBudgetsByProjectId: builder.query({
      query: ({
        projectId,
        page = 1,
        pageSize = 10,
        search = "",
      }: {
        projectId: string;
        page?: number;
        pageSize?: number;
        search?: string;
      }) => {
        const params = new URLSearchParams();
        if (page) params.set("page", String(page));
        if (pageSize) params.set("page_size", String(pageSize));
        if (search) params.set("search", search);
        const q = params.toString();
        return `/organization/api/v1/project-budgets/by-project/${projectId}/${q ? `?${q}` : ""}`;
      },
      providesTags: (result, error, { projectId }) => [
        { type: "GetProjectBudgets", id: projectId },
      ],
    }),
    createProjectBudget: builder.mutation({
      query: (payload: {
        project: number;
        section_name: string;
        allocated_cost: string | number;
        actual_cost?: string | number;
        status?: string;
        description?: string;
      }) => ({
        url: "/organization/api/v1/project-budgets/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetProjectBudgets", id: String(arg.project) },
        "GetProjectsById",
      ],
    }),
    updateProjectBudget: builder.mutation({
      query: ({
        id,
        data,
      }: {
        id: number;
        data: Partial<{
          section_name: string;
          allocated_cost: string | number;
          actual_cost: string | number;
          status: string;
          description: string;
        }>;
      }) => ({
        url: `/organization/api/v1/project-budgets/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: () => ["GetProjectBudgets", "GetProjectsById"],
    }),
    deleteProjectBudget: builder.mutation({
      query: (id: number) => ({
        url: `/organization/api/v1/project-budgets/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["GetProjectBudgets", "GetProjectsById"],
    }),
    bulkImportProjectBudget: builder.mutation({
      query: ({ project, file }: { project: number | string; file: File }) => {
        const formData = new FormData();
        formData.append("project", String(project));
        formData.append("file", file);
        return {
          url: "/organization/api/v1/project-budgets/bulk-import/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "GetProjectBudgets", id: String(arg.project) },
        "GetProjectsById",
      ],
    }),

    // Project Budget Cost Mappings (allocations per section)
    getProjectBudgetMappingsByProjectId: builder.query({
      query: (projectId: string) =>
        `/organization/api/v1/project-budget-cost-mappings/?project=${projectId}`,
      providesTags: (result, error, projectId) => [
        { type: "GetProjectBudgets", id: projectId },
      ],
    }),

    getProjectProductAssignmentsByProjectId: builder.query({
      query: ({
        projectId,
        page = 1,
        pageSize = 10,
        search = "",
      }: {
        projectId: string;
        page?: number;
        pageSize?: number;
        search?: string;
      }) => {
        const params = new URLSearchParams();
        if (page) params.set("page", String(page));
        if (pageSize) params.set("page_size", String(pageSize));
        if (search) params.set("search", search);
        const q = params.toString();
        return `/organization/api/v1/project-product-assignments/by-project/${projectId}/${q ? `?${q}` : ""}`;
      },
      providesTags: (result, error, { projectId }) => [
        { type: "GetProjectProductAssignments", id: projectId },
      ],
    }),
    createProjectProductAssignment: builder.mutation({
      query: (payload: {
        project: number;
        product: number;
        maximum_quantity: string | number;
        remarks?: string;
      }) => ({
        url: "/organization/api/v1/project-product-assignments/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetProjectProductAssignments", id: String(arg.project) },
        "GetProjectsById",
      ],
    }),
    updateProjectProductAssignment: builder.mutation({
      query: ({
        id,
        data,
      }: {
        id: number;
        data: Partial<{
          product: number;
          maximum_quantity: string | number;
          remarks: string;
        }>;
      }) => ({
        url: `/organization/api/v1/project-product-assignments/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["GetProjectProductAssignments", "GetProjectsById"],
    }),
    getProjectProductAssignmentQuantityHistory: builder.query({
      query: (id: number) =>
        `/organization/api/v1/project-product-assignments/${id}/quantity-history/`,
    }),
    deleteProjectProductAssignment: builder.mutation({
      query: (id: number) => ({
        url: `/organization/api/v1/project-product-assignments/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["GetProjectProductAssignments", "GetProjectsById"],
    }),
    publishProjectProductAssignment: builder.mutation({
      query: (id: number) => ({
        url: `/organization/api/v1/project-product-assignments/${id}/publish/`,
        method: "POST",
      }),
      invalidatesTags: ["GetProjectProductAssignments", "GetProjectsById"],
    }),
    bulkPublishProjectProductAssignments: builder.mutation({
      query: ({
        project,
        assignment_ids,
      }: {
        project: number | string;
        assignment_ids: number[];
      }) => ({
        url: "/organization/api/v1/project-product-assignments/bulk-publish/",
        method: "POST",
        body: { project, assignment_ids },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetProjectProductAssignments", id: String(arg.project) },
        "GetProjectsById",
      ],
    }),
    bulkImportProjectProductAssignment: builder.mutation({
      query: ({ project, file }: { project: number | string; file: File }) => {
        const formData = new FormData();
        formData.append("project", String(project));
        formData.append("file", file);
        return {
          url: "/organization/api/v1/project-product-assignments/bulk-import/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "GetProjectProductAssignments", id: String(arg.project) },
        "GetProjectsById",
      ],
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useGetAllProjectsQuery,
  useGetAllProjectsByUserRoleQuery,
  useGetProjectsByAssignedUserQuery,
  useGetprojectByIdQuery,
  useGetProjectAttachmentByIdQuery,
  useGetAllProjectAttachmentsQuery,
  useGetProjectsByDropDownQuery,
  useGetTeamByProjectIdQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useGetProjectByEmployeeIdQuery,
  useCreateProjectAttachmentMutation,
  useDeleteProjectAttachmentByIdMutation,
  useDeleteTeamByIdMutation,
  useGetProjectBudgetsByProjectIdQuery,
  useCreateProjectBudgetMutation,
  useUpdateProjectBudgetMutation,
  useDeleteProjectBudgetMutation,
  useBulkImportProjectBudgetMutation,
  useGetProjectBudgetMappingsByProjectIdQuery,
  useGetProjectProductAssignmentsByProjectIdQuery,
  useCreateProjectProductAssignmentMutation,
  useUpdateProjectProductAssignmentMutation,
  useGetProjectProductAssignmentQuantityHistoryQuery,
  useDeleteProjectProductAssignmentMutation,
  usePublishProjectProductAssignmentMutation,
  useBulkPublishProjectProductAssignmentsMutation,
  useBulkImportProjectProductAssignmentMutation,
} = projectSlice;
